import React from "react"
import { isMobileOnly } from "react-device-detect"
import clsx from "clsx"
import { useTranslation } from "react-i18next"
import FormElement from "components/Form/FormElement"
import * as BicytrustApi from "features/bike/api/BicytrustApi"
import { InsuranceFormSteps } from "./InsuranceForm"
import { FormError } from "models/form"

export type QuoteFormFields = {
	warranty_type?: string
	warranty_type_name?: string
	bike_price?: number
	postal_code?: string
}

const defaultFormFields: QuoteFormFields = {
	warranty_type: "",
	warranty_type_name: "",
	bike_price: 0,
	postal_code: "",
}

export const InsuranceQuote = ({ onSubmit, locked, existingContract }) => {
	const { t } = useTranslation()

	const [formFields, setFormFields] = React.useState(
		existingContract
			? {
					warranty_type: existingContract.pack_id,
					bike_price: existingContract.bike_price,
					postal_code: existingContract.postal_code,
				}
			: defaultFormFields
	)

	const [disabled, setDisabled] = React.useState(false)
	const [errors, setErrors] = React.useState([])
	const [showLocalErrors, setShowLocalErrors] = React.useState(false)

	const [packs, setPacks] = React.useState(null)

	React.useEffect(() => {
		const isActionDisabled =
			locked || (existingContract && existingContract.status !== InsuranceFormSteps.CANCELED)
		setDisabled(isActionDisabled)
	}, [locked, existingContract])

	const fetchPacks = React.useCallback(
		() => {
			;(async () => {
				try {
					const { res: getPacks, getError } = await BicytrustApi.getPacks()
					if (getError()) throw getError()
					const mappedPacks = getPacks.map((pack) => {
						return {
							label: pack.pack_name,
							value: pack.pack_id,
						}
					})
					setPacks(mappedPacks)
					setFormFields({
						...formFields,
						warranty_type: getPacks[0].pack_id,
						warranty_type_name: getPacks[0].pack_name,
					})
				} catch (error) {
					console.error(error)
				}
			})()
		},
		[] // eslint-disable-line
	)

	React.useEffect(() => fetchPacks(), [])

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const target = event.target
		const value = target.type === "checkbox" ? target.checked : target.value
		const name = target.name

		setFormFields({
			...formFields,
			[name]: value,
		})
	}

	const handleValidation = React.useCallback((name: string, errs: Array<FormError>) => {
		setErrors((s) => {
			const cleanErrors = [...s.filter((e) => e.origin !== name)]
			return [...cleanErrors, ...errs]
		})
	}, [])

	const handleSubmit = (event: React.SyntheticEvent) => {
		event.preventDefault()
		const formReady = !(errors.length > 0)

		if (!formReady) {
			setShowLocalErrors(true)
		} else {
			setShowLocalErrors(false)

			onSubmit(formFields)
		}
	}

	const formElementCommonProps = {
		showErrors: showLocalErrors,
		onValidate: handleValidation,
		onChange: handleInputChange,
	}

	return (
		<div data-cy="form-bike-insurance" className="mb-8">
			<FormElement
				value={formFields.warranty_type}
				name="warranty_type"
				type="select"
				defaultValue={
					packs
						? {
								label: packs[0]?.label,
								value: packs[0]?.value,
							}
						: {}
				}
				options={packs}
				label={t("insurance.warranty_type", { ns: "bike" })}
				className={clsx("mb-7 w-1/2 mr-2", { "w-full mr-0": isMobileOnly })}
				required={true}
				disabled
				{...formElementCommonProps}
			/>

			<div className={clsx("flex", { "flex-col": isMobileOnly })}>
				<FormElement
					value={formFields.bike_price}
					name="bike_price"
					type="number"
					label={t("insurance.bike_price", { ns: "bike" })}
					className={clsx("mb-7 w-1/2", {
						"w-full mr-0": isMobileOnly,
						"mr-2": !isMobileOnly,
					})}
					required={true}
					helpText={t("insurance.bike_price_help", { ns: "bike" })}
					customRules={[
						{
							key: "price-invalid",
							check: (value) => value > 0 && value < 9001,
						},
					]}
					disabled={disabled}
					max={9001}
					{...formElementCommonProps}
				/>

				<FormElement
					value={formFields.postal_code}
					name="postal_code"
					type="number"
					label={t("insurance.postal_code", { ns: "bike" })}
					className={clsx("mb-7  w-1/2", {
						"w-full ml-0": isMobileOnly,
						"ml-2": !isMobileOnly,
					})}
					required={true}
					customRules={[
						{
							key: "postal-invalid",
							check: (value) => /^(?:[0-8]\d|9[0-8])\d{3}$/.test(value),
						},
					]}
					disabled={disabled}
					max={99999}
					{...formElementCommonProps}
				/>
			</div>

			{!disabled && (
				<div className={clsx({ "flex flex-col": isMobileOnly })}>
					<p className="c-p mb-5">{t("insurance.next_disclaimer", { ns: "bike" })}</p>

					<button
						type="button"
						data-cy="insurance-next"
						className="c-btn c-btn--primary uppercase c-btn--m px-7 float-right"
						onClick={handleSubmit}
						disabled={errors.length > 0}
					>
						{t("insurance.next", { ns: "bike" })}
					</button>
				</div>
			)}
		</div>
	)
}
